import React from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Eye, EyeOff, Check, X } from 'lucide-react';
import { theme } from '@shared/styles/theme';
import { styles } from './RegisterForm.styles';

interface PasswordRequirement {
  label: string;
  met: boolean;
}

export interface PasswordFieldsProps {
  password: string;
  confirmPassword: string;
  showPassword: boolean;
  showConfirmPassword: boolean;
  isLoading: boolean;
  onPasswordChange: (value: string) => void;
  onConfirmPasswordChange: (value: string) => void;
  onToggleShowPassword: () => void;
  onToggleShowConfirmPassword: () => void;
}

function getConfirmPasswordBorderColor(confirmPassword: string, passwordsMatch: boolean): string {
  if (!confirmPassword) return theme.colors.border.light;
  if (passwordsMatch) return theme.colors.success;
  return theme.colors.error;
}

function getPasswordRequirements(password: string, t: TFunction): PasswordRequirement[] {
  return [
    { label: t('passwordRequirements.min8Chars'), met: password.length >= 8 },
    { label: t('passwordRequirements.oneUppercase'), met: /[A-Z]/.test(password) },
    { label: t('passwordRequirements.oneLowercase'), met: /[a-z]/.test(password) },
    { label: t('passwordRequirements.oneNumber'), met: /\d/.test(password) },
    { label: t('passwordRequirements.oneSpecial'), met: /[!@#$%^&*]/.test(password) },
  ];
}

/**
 * PasswordFields component - Renders password input with requirements checker
 */
export const PasswordFields: React.FC<PasswordFieldsProps> = ({
  password,
  confirmPassword,
  showPassword,
  showConfirmPassword,
  isLoading,
  onPasswordChange,
  onConfirmPasswordChange,
  onToggleShowPassword,
  onToggleShowConfirmPassword,
}) => {
  const { t } = useTranslation('auth');

  const passwordRequirements = getPasswordRequirements(password, t);
  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;

  return (
    <>
      <div style={styles.formGroup}>
        <label htmlFor="password" style={styles.label}>
          {t('register.passwordLabel')}
        </label>
        <div style={styles.passwordWrapper}>
          <input
            id="password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => onPasswordChange(e.target.value)}
            placeholder={t('register.passwordPlaceholder')}
            disabled={isLoading}
            required
            style={styles.passwordInput}
            className="minecraft-input"
          />
          <button
            type="button"
            onClick={onToggleShowPassword}
            style={styles.eyeButton}
            aria-label={
              showPassword ? t('accessibility.hidePassword') : t('accessibility.showPassword')
            }
          >
            {showPassword ? (
              <EyeOff size={20} color={theme.colors.text.secondary} aria-hidden="true" />
            ) : (
              <Eye size={20} color={theme.colors.text.secondary} aria-hidden="true" />
            )}
          </button>
        </div>

        {password && (
          <div style={styles.requirementsContainer}>
            {passwordRequirements.map((req) => (
              <div key={req.label} style={styles.requirementRow}>
                {req.met ? (
                  <Check size={14} color={theme.colors.success} aria-hidden="true" />
                ) : (
                  <X size={14} color={theme.colors.text.secondary} aria-hidden="true" />
                )}
                <span
                  style={{
                    ...styles.requirementText,
                    color: req.met ? theme.colors.success : theme.colors.text.secondary,
                  }}
                >
                  {req.label}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={styles.formGroup}>
        <label htmlFor="confirmPassword" style={styles.label}>
          {t('register.confirmPasswordLabel')}
        </label>
        <div style={styles.passwordWrapper}>
          <input
            id="confirmPassword"
            type={showConfirmPassword ? 'text' : 'password'}
            value={confirmPassword}
            onChange={(e) => onConfirmPasswordChange(e.target.value)}
            placeholder={t('register.confirmPasswordPlaceholder')}
            disabled={isLoading}
            required
            style={{
              ...styles.passwordInput,
              borderColor: getConfirmPasswordBorderColor(confirmPassword, passwordsMatch),
            }}
            className="minecraft-input"
          />
          <button
            type="button"
            onClick={onToggleShowConfirmPassword}
            style={styles.eyeButton}
            aria-label={
              showConfirmPassword
                ? t('accessibility.hidePassword')
                : t('accessibility.showPassword')
            }
          >
            {showConfirmPassword ? (
              <EyeOff size={20} color={theme.colors.text.secondary} aria-hidden="true" />
            ) : (
              <Eye size={20} color={theme.colors.text.secondary} aria-hidden="true" />
            )}
          </button>
        </div>
        {confirmPassword && !passwordsMatch && (
          <p style={styles.errorText}>{t('errors.passwordsDontMatch')}</p>
        )}
      </div>
    </>
  );
};
